import { useEffect, useState } from "react";
import { Download, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  downloadBourseDocument,
  deleteBourseDemande,
  formatApiError,
  getBourseDemandesAdmin,
  getCampagneBourseAdmin,
  updateBourseStatut,
  updateCampagneBourse,
} from "@/lib/api";

const STATUTS = {
  recue: "Reçue",
  en_analyse: "En analyse",
  documents_a_completer: "Documents à compléter",
  acceptee: "Acceptée",
  refusee: "Refusée",
};

const inputCls = "w-full rounded-sm border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary";
const labelCls = "text-xs font-semibold uppercase tracking-[0.14em] text-foreground/50";

export const AdminBourses = () => {
  const [campagne, setCampagne] = useState(null);
  const [demandes, setDemandes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [sauvegarde, setSauvegarde] = useState(false);

  const charger = async () => {
    setChargement(true);
    try {
      const [c, d] = await Promise.all([getCampagneBourseAdmin(), getBourseDemandesAdmin()]);
      setCampagne(c);
      setDemandes(d);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => { charger(); }, []);
  if (chargement || !campagne) return <p className="text-sm text-foreground/50" data-testid="admin-bourses-loading">Chargement des bourses…</p>;

  const setCampagneField = (key, value) => setCampagne((c) => ({ ...c, [key]: value }));

  const sauvegarderCampagne = async () => {
    setSauvegarde(true);
    try {
      const res = await updateCampagneBourse({
        titre: campagne.titre,
        description: campagne.description || null,
        date_debut: campagne.date_debut || null,
        date_fin: campagne.date_fin || null,
        ouverte_manuelle: campagne.ouverte_manuelle,
      });
      setCampagne(res);
      toast.success(res.est_ouverte ? "Campagne ouverte" : "Campagne fermée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSauvegarde(false);
    }
  };

  const changerStatut = async (id, statut) => {
    try {
      const res = await updateBourseStatut(id, statut);
      setDemandes((items) => items.map((item) => (item.id === id ? res : item)));
      toast.success("Statut mis à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const supprimer = async (id) => {
    try {
      await deleteBourseDemande(id);
      setDemandes((items) => items.filter((item) => item.id !== id));
      toast.success("Demande supprimée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const telecharger = async (doc) => {
    try {
      const blob = await downloadBourseDocument(doc.document_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.nom_original;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de télécharger le document");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-bourses">
      <section className="border border-border bg-background p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl font-semibold">Campagne de bourse</h3>
            <p className="mt-2 text-sm text-foreground/55">Le formulaire public s'affiche seulement lorsque la campagne est ouverte.</p>
          </div>
          <span className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] ${campagne.est_ouverte ? "bg-primary/10 text-primary" : "bg-foreground/10 text-foreground/50"}`} data-testid="admin-campagne-status">
            {campagne.est_ouverte ? "Ouverte" : "Fermée"}
          </span>
        </div>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <div><label className={labelCls}>Titre</label><input value={campagne.titre} onChange={(e) => setCampagneField("titre", e.target.value)} className={`${inputCls} mt-2`} data-testid="admin-campagne-titre" /></div>
          <label className="mt-6 flex items-center gap-3 text-sm font-semibold" data-testid="admin-campagne-ouverte-label">
            <input type="checkbox" checked={campagne.ouverte_manuelle} onChange={(e) => setCampagneField("ouverte_manuelle", e.target.checked)} className="accent-primary" data-testid="admin-campagne-ouverte" />
            Autoriser les demandes
          </label>
          <div><label className={labelCls}>Date de début</label><input type="date" value={campagne.date_debut || ""} onChange={(e) => setCampagneField("date_debut", e.target.value)} className={`${inputCls} mt-2`} data-testid="admin-campagne-date-debut" /></div>
          <div><label className={labelCls}>Date de fin</label><input type="date" value={campagne.date_fin || ""} onChange={(e) => setCampagneField("date_fin", e.target.value)} className={`${inputCls} mt-2`} data-testid="admin-campagne-date-fin" /></div>
          <div className="md:col-span-2"><label className={labelCls}>Description</label><textarea rows={3} value={campagne.description || ""} onChange={(e) => setCampagneField("description", e.target.value)} className={`${inputCls} mt-2 resize-y`} data-testid="admin-campagne-description" /></div>
        </div>
        <button onClick={sauvegarderCampagne} disabled={sauvegarde} className="mt-6 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60" data-testid="admin-campagne-save">
          {sauvegarde ? "Enregistrement…" : "Enregistrer la campagne"}
        </button>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl font-semibold">Demandes reçues ({demandes.length})</h3>
          <button onClick={charger} className="flex items-center gap-2 border border-border px-4 py-2 text-xs font-semibold" data-testid="admin-bourses-refresh"><RefreshCw size={14} /> Actualiser</button>
        </div>
        {demandes.length === 0 && <p className="border border-dashed border-border p-8 text-sm text-foreground/50" data-testid="admin-bourses-empty">Aucune demande pour l'instant.</p>}
        {demandes.map((d, i) => (
          <article key={d.id} className="border border-border bg-background p-6" data-testid={`admin-bourse-demande-${i}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{d.type_demandeur === "obnl" ? "OBNL" : d.type_demandeur === "societe" ? "Société par actions" : "Personne entrepreneure"}</p>
                <h4 className="mt-2 font-serif text-2xl font-semibold">{d.titre_projet}</h4>
                <p className="mt-2 text-sm text-foreground/55">
                  {d.prenom} {d.nom}{d.organisation ? ` · ${d.organisation}` : ""} · {d.courriel} · {d.telephone}
                </p>
                {d.representant && (
                  <p className="mt-2 text-sm text-foreground/55" data-testid={`admin-bourse-representant-${i}`}>
                    Représentant : {d.representant.prenom} {d.representant.nom}, {d.representant.titre} · {d.representant.courriel} · {d.representant.telephone}
                  </p>
                )}
                {d.membres_ca?.length > 0 && (
                  <div className="mt-3 text-sm text-foreground/55" data-testid={`admin-bourse-ca-${i}`}>
                    <p className="font-semibold text-foreground/70">Conseil d'administration ({d.membres_ca.length})</p>
                    <ul className="mt-1 space-y-1">
                      {d.membres_ca.map((m, j) => (
                        <li key={`${m.nom}-${j}`}>{m.prenom} {m.nom} · {m.role} · né(e) le {new Date(`${m.date_naissance}T12:00:00`).toLocaleDateString("fr-CA", { dateStyle: "long" })}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="mt-1 text-sm text-foreground/55">{new Date(d.date_soumission).toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" })} · {d.montant_demande.toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}</p>
              </div>
              <div className="flex gap-2">
                <select value={d.statut} onChange={(e) => changerStatut(d.id, e.target.value)} className="rounded-sm border border-border bg-card px-4 py-2 text-sm font-semibold" data-testid={`admin-bourse-statut-${i}`}>
                  {Object.entries(STATUTS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <button onClick={() => supprimer(d.id)} className="rounded-sm border border-destructive/40 px-3 py-2 text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground" aria-label="Supprimer la demande" data-testid={`admin-bourse-delete-${i}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            <div className="mt-5 grid gap-4 text-sm leading-relaxed text-foreground/70 md:grid-cols-2">
              <p><strong>Projet :</strong> {d.description_projet}</p>
              <p><strong>Besoin :</strong> {d.besoin_financier}</p>
              <p><strong>Impact :</strong> {d.impact_attendu}</p>
              <p><strong>Utilisation :</strong> {d.utilisation_fonds}</p>
              {d.chiffre_affaires_realise && <p><strong>Chiffre d'affaires :</strong> {d.chiffre_affaires_realise}</p>}
            </div>
            <div className="mt-5 flex flex-wrap gap-2" data-testid={`admin-bourse-documents-${i}`}>
              {d.documents.map((doc, j) => (
                <button key={doc.document_id} onClick={() => telecharger(doc)} className="flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-xs font-semibold transition-colors hover:border-primary hover:text-primary" data-testid={`admin-bourse-document-${i}-${j}`}>
                  <Download size={14} /> {doc.nom_original}
                </button>
              ))}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
};
